import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import Clients from './../../../assets/clients/clients.json';
import { ClientConstant, CommonConstants } from 'src/app/shared/constant';
import { EncryptionService } from './encryption.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { KeyValue } from '@angular/common';
import {
  Client,
  IAddressDto,
  IClient,
  IClientOutlet,
  INotificationDto,
  IOutletFeaturesDto,
} from '../models';

@Injectable()
export class ClientService {
  private readonly clientKey: string = CommonConstants.clientKey;

  private currentClientSubject: BehaviorSubject<Client>;
  public currentClient: Observable<Client>;

  private isOutletSelectedSubject = new ReplaySubject<boolean>(1);
  public isOutletSelected = this.isOutletSelectedSubject.asObservable();

  private selectedOutletSubject = new BehaviorSubject<number>(null);
  public selectedOutlet = this.selectedOutletSubject.asObservable();

  private outletFeatureSubject = new BehaviorSubject<IOutletFeaturesDto>(null);
  public outletFeature = this.outletFeatureSubject.asObservable();

  private notificationSubject = new ReplaySubject<INotificationDto>(null);
  public notification = this.notificationSubject.asObservable();

  constructor(
    private readonly encryptionService: EncryptionService,
    private readonly httpService: HttpClient
  ) {
    this.currentClientSubject = new BehaviorSubject<Client>(this.getClient());
    this.currentClient = this.currentClientSubject.asObservable();
  }

  public get currentClientValue(): Client {
    return this.currentClientSubject.value;
  }

  public setCurrentClient(client: Client, isLongTerm: boolean): void {
    this.currentClientSubject.next(client);

    //const json = JSON.stringify(client);
    const json = this.encryptionService.setJson(client);

    if (isLongTerm) {
      localStorage.setItem(this.clientKey, json);
    } else {
      sessionStorage.setItem(this.clientKey, json);
    }
  }

  public getClient(): Client {
    let storedClient: any = localStorage.getItem(this.clientKey);

    if (storedClient == null) {
      storedClient = sessionStorage.getItem(this.clientKey);
    }

    if (storedClient == null) {
      return null;
    }

    //const json: any = JSON.parse(storedClient);
    const json = this.encryptionService.getJson(storedClient);
    const client: Client = new Client(json);

    return client;
  }

  public setSelectedOutlet(outletId: number) {
    this.selectedOutletSubject.next(outletId);
  }

  public setOutletFeatures(value: IOutletFeaturesDto): void {
    this.outletFeatureSubject.next(value);
  }

  public setNotification(value: INotificationDto) {
    this.notificationSubject.next(value);
  }

  public get selectedOutletValue(): number {
    return this.selectedOutletSubject.value;
  }

  public get outletFeatureValue(): IOutletFeaturesDto {
    return this.outletFeatureSubject.value;
  }

  public setOutletIn(value: boolean): void {
    this.isOutletSelectedSubject.next(value);
  }

  updateClientOutlet(outlet: IClientOutlet) {
    let client = this.getClient();
    if (outlet) {
      client.outlet = outlet;
      this.setCurrentClient(client, false);
      this.setOutletIn(true);
    } else {
      client.outlet = {} as IClientOutlet;
      this.setCurrentClient(client, false);
      this.setOutletIn(false);
    }
  }

  updateClientLocation(address: IAddressDto) {
    let client = this.getClient();
    client.location = address;
    this.setCurrentClient(client, false);
  }

  updateClientImage(bgImage: string) {
    let client = this.getClient();
    client.style.bgImage = bgImage;
    this.setCurrentClient(client, false);
  }

  async loadClients(source: number): Promise<boolean> {
    let client: IClient;
    let url = window.location.origin;
    url = url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').split('/')[0];

    let setClient = () => {
      if (client) {
        client.active = true;
        client.style = {
          bg: {
            'background-color': client.color,
          },
          color: { color: client.color },
          bgImage: null,
        };
      }
      let _client = this.getClient();
      if (_client?.active) {
        client = _client;
      }
      this.setCurrentClient(client, false);
    };

    if (source === ClientConstant.ClientSource.Assets) {
      client = Clients.find((x) => x.webUrl == url);
      setClient();
      return new Promise((resolve) => {
        resolve(true);
      });
    } else if (source === ClientConstant.ClientSource.AzureBlob) {
      return this.httpService
        .get<IClient[]>(environment.clients)
        .toPromise()
        .then((data) => {
          client = data.find((x) => x.webUrl == url);
          setClient();
        })
        .then((_) => Promise.resolve(true))
        .catch((error) => {
          console.error('Error loading info', error);
          return Promise.resolve(false);
        });
    } else if (source === ClientConstant.ClientSource.Database) {
      const model = { key: 0, value: url } as KeyValue<number, string>;
      return this.httpService
        .post<IClient>(`${environment.api_url}/common/client`, model)
        .toPromise()
        .then((data) => {
          client = data;
          setClient();
        })
        .then((_) => Promise.resolve(true))
        .catch((error) => {
          console.error('Error loading info', error);
          return Promise.resolve(false);
        });
    } else {
      return new Promise((resolve) => {
        resolve(false);
      });
    }
  }
}
